import React, { useState } from "react";
import axios from "axios";
import { useLang } from "../../context/LanguageContext";
import { MapPin, Phone, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const WEB3FORMS_ACCESS_KEY = "d2daf9f8-b9d7-4861-b942-c42b6f4f3284";
const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";

const initialState = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const payload = {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: form.subject
          ? `LAWSN · ${form.subject}`
          : "LAWSN · Ново запитване от сайта",
        from_name: "LAWSN website",
        name: form.name,
        email: form.email,
        phone: form.phone || "—",
        language: lang,
        message: form.message,
        botcheck: "",
      };
      const { data } = await axios.post(WEB3FORMS_ENDPOINT, payload, {
        headers: { "Content-Type": "application/json", Accept: "application/json" },
      });
      if (data && data.success) {
        toast.success(t.contact.form.success);
        setForm(initialState);
      } else {
        toast.error(t.contact.form.error);
      }
    } catch (err) {
      console.error(err);
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-ink-900 text-cream-100"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: Title + Info */}
          <div className="lg:col-span-5">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-cream-100/60 mb-6">
              {t.contact.overline}
            </div>
            <h2
              data-testid="contact-title"
              className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
            >
              {t.contact.title}
            </h2>
            <p className="mt-6 text-base md:text-lg text-cream-100/70 leading-relaxed max-w-md">
              {t.contact.lead}
            </p>

            <dl className="mt-14 space-y-8">
              <div className="flex gap-5">
                <MapPin size={18} className="mt-1 text-crimson-light shrink-0" />
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream-100/50 mb-2">
                    {t.contact.address_label}
                  </dt>
                  <dd className="text-sm md:text-base text-cream-100/90 whitespace-pre-line leading-relaxed">
                    {t.contact.address}
                  </dd>
                </div>
              </div>
              <div className="flex gap-5">
                <Phone size={18} className="mt-1 text-crimson-light shrink-0" />
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream-100/50 mb-2">
                    {t.contact.phone_label}
                  </dt>
                  <dd>
                    <a
                      data-testid="contact-phone"
                      href="tel:+359887553482"
                      className="text-sm md:text-base text-cream-100/90 hover:text-crimson-light transition-colors"
                    >
                      +359 88 755 3482
                    </a>
                  </dd>
                </div>
              </div>
              <div className="flex gap-5">
                <Mail size={18} className="mt-1 text-crimson-light shrink-0" />
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream-100/50 mb-2">
                    {t.contact.email_label}
                  </dt>
                  <dd>
                    <a
                      data-testid="contact-email"
                      href="mailto:office@lawsn.com"
                      className="text-sm md:text-base text-cream-100/90 hover:text-crimson-light transition-colors"
                    >
                      office@lawsn.com
                    </a>
                  </dd>
                </div>
              </div>
            </dl>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10"
            >
              <Field
                id="name"
                name="name"
                label={t.contact.form.name}
                value={form.name}
                onChange={onChange}
                required
                testid="form-name"
              />
              <Field
                id="email"
                name="email"
                type="email"
                label={t.contact.form.email}
                value={form.email}
                onChange={onChange}
                required
                testid="form-email"
              />
              <Field
                id="phone"
                name="phone"
                label={t.contact.form.phone}
                value={form.phone}
                onChange={onChange}
                testid="form-phone"
              />
              <Field
                id="subject"
                name="subject"
                label={t.contact.form.subject}
                value={form.subject}
                onChange={onChange}
                testid="form-subject"
              />
              <Field
                id="message"
                name="message"
                label={t.contact.form.message}
                value={form.message}
                onChange={onChange}
                required
                textarea
                className="md:col-span-2"
                testid="form-message"
              />

              <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-2">
                <p className="text-xs text-cream-100/50 max-w-sm leading-relaxed">
                  {t.contact.form.consent}
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="group inline-flex items-center gap-3 px-7 py-3.5 bg-cream-100 text-ink-900 hover:bg-crimson hover:text-cream-100 transition-colors text-sm font-medium tracking-wide disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? t.contact.form.sending : t.contact.form.submit}
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({
  id,
  name,
  label,
  value,
  onChange,
  type = "text",
  required = false,
  textarea = false,
  className = "",
  testid,
}) {
  const baseInput =
    "w-full bg-transparent border-0 border-b border-cream-100/25 focus:border-cream-100 focus:outline-none focus:ring-0 px-0 py-3 text-cream-100 placeholder:text-cream-100/30 transition-colors";
  return (
    <div className={`relative ${className}`}>
      <label
        htmlFor={id}
        className="block font-mono text-[10px] uppercase tracking-[0.25em] text-cream-100/50 mb-2"
      >
        {label} {required && <span className="text-crimson-light">*</span>}
      </label>
      {textarea ? (
        <textarea
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          rows={4}
          data-testid={testid}
          className={`${baseInput} resize-none`}
        />
      ) : (
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          data-testid={testid}
          className={baseInput}
        />
      )}
    </div>
  );
}
